import {
  AlignmentType,
  Document,
  HeadingLevel,
  IRunOptions,
  NumberFormat,
  Packer,
  Paragraph,
  TextRun,
  UnderlineType,
} from "docx";

interface NumberFormats {
  [key: string]: (typeof NumberFormat)[keyof typeof NumberFormat];
}

interface Indentation {
  [key: number]: number;
}

interface ListCounter {
  current: number;
}

interface TextFormatting {
  bold?: boolean;
  italics?: boolean;
  underline?: (typeof UnderlineType)[keyof typeof UnderlineType];
}

interface NumberingLevel {
  level: number;
  format: (typeof NumberFormat)[keyof typeof NumberFormat];
  text: string;
  alignment: (typeof AlignmentType)[keyof typeof AlignmentType];
  style: {
    paragraph: {
      indent: { left: number; hanging: number };
    };
  };
}

interface CustomNumberingConfig {
  id?: string;
  reference: string;
  levels: NumberingLevel[];
}

// Shared format configurations
const numberFormats: NumberFormats = {
  decimal: NumberFormat.DECIMAL,
  i: NumberFormat.LOWER_ROMAN,
  I: NumberFormat.UPPER_ROMAN,
  a: NumberFormat.LOWER_LETTER,
  A: NumberFormat.UPPER_LETTER,
  undefined: NumberFormat.DECIMAL, // default if no type specified
};

const indentation: Indentation = {
  0: 720,
  1: 1440,
  2: 2160,
  3: 2880,
};

interface Sections extends Array<Paragraph> {
  numberingConfigs?: CustomNumberingConfig[];
}

// Add mapping for all heading levels
const headingLevelMap: Record<
  string,
  { level: (typeof HeadingLevel)[keyof typeof HeadingLevel]; size: number }
> = {
  h1: { level: HeadingLevel.HEADING_1, size: 36 },
  h2: { level: HeadingLevel.HEADING_2, size: 32 },
  h3: { level: HeadingLevel.HEADING_3, size: 30 },
  h4: { level: HeadingLevel.HEADING_4, size: 28 },
  h5: { level: HeadingLevel.HEADING_5, size: 26 },
  h6: { level: HeadingLevel.HEADING_6, size: 24 },
};

function createTextRun(text: string, formatting: TextFormatting = {}): TextRun {
  const options = {
    text: text,
    size: 24,
    font: "Times New Roman",
    bold: formatting.bold,
    italics: formatting.italics,
    underline: formatting.underline
      ? {
          type: formatting.underline,
        }
      : undefined,
  } as IRunOptions;

  return new TextRun(options);
}

// Helper function to process text with inline formatting - now iterative
function processInlineFormatting(node: Node): TextRun[] {
  const runs: TextRun[] = [];

  function processNode(currentNode: Node, formatting: TextFormatting = {}) {
    if (currentNode.nodeType === Node.TEXT_NODE) {
      const text = currentNode.textContent;
      if (text) {
        // Preserve whitespace in text nodes
        runs.push(createTextRun(text, formatting));
      }
    } else if (currentNode.nodeType === Node.ELEMENT_NODE) {
      const element = currentNode as Element;
      const nodeName = element.nodeName.toLowerCase();

      // Accumulate formatting properties
      const newFormatting = { ...formatting };

      switch (nodeName) {
        case "strong":
        case "b":
          newFormatting.bold = true;
          break;
        case "em":
        case "i":
          newFormatting.italics = true;
          break;
        case "span":
          // Process span content but maintain existing whitespace
          Array.from(element.childNodes).forEach((child) => {
            processNode(child, formatting);
          });
          return;
      }

      // Process child nodes with accumulated formatting
      Array.from(element.childNodes).forEach((child) => {
        processNode(child, newFormatting);
      });
    }
  }

  processNode(node);
  return runs;
}

function createUnorderedListItem(
  textRuns: TextRun[],
  level: number,
): Paragraph {
  return new Paragraph({
    children: textRuns,
    bullet: {
      level,
    },
    spacing: { before: 120, after: 120 },
    indent: { left: indentation[level], hanging: 360 },
  });
}

function createOrderedListItem(
  textRuns: TextRun[],
  level: number,
  listId: number,
): Paragraph {
  return new Paragraph({
    children: textRuns,
    numbering: {
      reference: `list-${listId}`,
      level,
    },
    spacing: { before: 120, after: 120 },
    indent: { left: indentation[level], hanging: 360 },
  });
}

function processUnorderedList(
  listNode: Element,
  sections: Sections,
  depth: number = 0,
  listCounter: ListCounter,
): void {
  const items = Array.from(listNode.children);

  for (const li of items) {
    // Get all text nodes and inline elements before any nested lists
    const textNodes = Array.from(li.childNodes).filter(
      (node) =>
        node.nodeType === Node.TEXT_NODE ||
        (node.nodeType === Node.ELEMENT_NODE &&
          !["ul", "ol"].includes((node as Element).nodeName.toLowerCase())),
    );

    if (textNodes.length > 0) {
      const container = document.createElement("div");
      textNodes.forEach((node) => container.appendChild(node.cloneNode(true)));
      const textRuns = processInlineFormatting(container);
      sections.push(createUnorderedListItem(textRuns, depth));
    }

    // Handle nested lists
    const nestedLists = Array.from(li.children).filter((child) =>
      ["ul", "ol"].includes(child.nodeName.toLowerCase()),
    );

    for (const nestedList of nestedLists) {
      if (nestedList.nodeName.toLowerCase() === "ul") {
        processUnorderedList(nestedList, sections, depth + 1, listCounter);
      } else {
        processOrderedList(nestedList, sections, depth + 1, listCounter);
      }
    }
  }
}

function processOrderedList(
  listNode: Element,
  sections: Sections,
  depth: number = 0,
  listCounter: ListCounter,
): void {
  const currentListId = listCounter.current++;
  const format = numberFormats[listNode.getAttribute("type") || "decimal"];

  const config: CustomNumberingConfig = {
    reference: `list-${currentListId}`,
    levels: [
      {
        level: 0,
        format,
        text: "%1.",
        alignment: AlignmentType.START,
        style: {
          paragraph: {
            indent: { left: indentation[0], hanging: 360 },
          },
        },
      },
      {
        level: 1,
        format: NumberFormat.LOWER_LETTER,
        text: "%2.",
        alignment: AlignmentType.START,
        style: {
          paragraph: {
            indent: { left: indentation[1], hanging: 360 },
          },
        },
      },
      {
        level: 2,
        format: NumberFormat.LOWER_ROMAN,
        text: "%3.",
        alignment: AlignmentType.START,
        style: {
          paragraph: {
            indent: { left: indentation[2], hanging: 360 },
          },
        },
      },
    ],
  };

  sections.numberingConfigs = sections.numberingConfigs || [];
  sections.numberingConfigs.push(config);

  const items = Array.from(listNode.children);

  for (const li of items) {
    // Get all text nodes and inline elements before any nested lists
    const textNodes = Array.from(li.childNodes).filter(
      (node) =>
        node.nodeType === Node.TEXT_NODE ||
        (node.nodeType === Node.ELEMENT_NODE &&
          !["ul", "ol"].includes((node as Element).nodeName.toLowerCase())),
    );

    if (textNodes.length > 0) {
      const container = document.createElement("div");
      textNodes.forEach((node) => container.appendChild(node.cloneNode(true)));
      const textRuns = processInlineFormatting(container);
      sections.push(createOrderedListItem(textRuns, depth, currentListId));
    }

    // Handle nested lists
    const nestedLists = Array.from(li.children).filter((child) =>
      ["ul", "ol"].includes(child.nodeName.toLowerCase()),
    );

    for (const nestedList of nestedLists) {
      if (nestedList.nodeName.toLowerCase() === "ul") {
        processUnorderedList(nestedList, sections, depth + 1, listCounter);
      } else {
        processOrderedList(nestedList, sections, depth + 1, listCounter);
      }
    }
  }
}

function processNode(
  node: Node,
  sections: Sections,
  listCounter: ListCounter,
): void {
  const nodeName = node.nodeName.toLowerCase();

  // Handle headings
  if (headingLevelMap[nodeName]) {
    const { level } = headingLevelMap[nodeName];
    sections.push(
      new Paragraph({
        children: processInlineFormatting(node),
        spacing: { before: 400, after: 200 },
        heading: level,
      }),
    );
    return;
  }

  switch (nodeName) {
    case "p":
      sections.push(
        new Paragraph({
          children: processInlineFormatting(node),
          spacing: { before: 120, after: 120 },
        }),
      );
      break;

    case "ol":
      processOrderedList(node as Element, sections, 0, listCounter);
      break;

    case "ul":
      processUnorderedList(node as Element, sections, 0, listCounter);
      break;

    case "hr":
      sections.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "_______________________________________________",
              size: 24,
            }),
          ],
          spacing: { before: 240, after: 240 },
          alignment: AlignmentType.CENTER,
        }),
      );
      break;

    default:
      if (node.childNodes?.length) {
        Array.from(node.childNodes).forEach((child) => {
          processNode(child, sections, listCounter);
        });
      }
  }
}

function createTitle(text: string): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: text.trim(),
        size: 40,
        bold: true,
        font: "Times New Roman",
      }),
    ],
    spacing: { before: 240, after: 400 },
  });
}

export async function generateDocx(
  contentNode: HTMLElement | null,
  title?: string,
): Promise<Blob | null> {
  if (!contentNode) return null;

  try {
    const sections: Sections = [];
    sections.numberingConfigs = [];
    const listCounter: ListCounter = { current: 0 };

    if (title) {
      sections.push(createTitle(title));
    }

    processNode(contentNode, sections, listCounter);

    const doc = new Document({
      numbering: {
        config: sections.numberingConfigs,
      },
      sections: [
        {
          properties: {},
          children: sections.filter((section) => !(section instanceof Array)),
        },
      ],
    });

    return await Packer.toBlob(doc);
  } catch (error) {
    console.error("Error generating document:", error);
    throw error;
  }
}
